import { MouseEventHandler, useEffect, useState } from 'react';
import FormCheckbox from '@base/components/src/FormCheckbox';
import AnchorDialog from '@base/components/src/AnchorDialog';
import CouponApplyErrorMessage from '../CouponApplyErrorMessage';
import { CouponApplyProps } from './types';
import clsx from 'clsx';
import styles from './CouponApply.module.css';

const CouponApply = ({ onApplyCouponToItem, productId, autoClipCoupon, onGetTermsContent }: CouponApplyProps) => {
  const [applyCoupon, setApplyCoupon] = useState(autoClipCoupon.applied);
  const [loading, setLoading] = useState(false);
  const [termsContent, setTermsContent] = useState<string | null>(null);
  const [applyCouponError, setApplyCouponError] = useState<string | null>(null);
  const handleApply = async () => {
    try {
      if (onApplyCouponToItem) {
        setLoading(true);
        await onApplyCouponToItem(autoClipCoupon.couponId, !applyCoupon);
        setApplyCoupon((prevApplyCoupon) => !prevApplyCoupon);
      }
    } catch (error) {
      if (error instanceof Error) {
        setApplyCouponError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setApplyCoupon(autoClipCoupon.applied);
  }, [autoClipCoupon.applied]);

  return (
    <>
      {applyCouponError && <CouponApplyErrorMessage message={applyCouponError} />}
      <div className={styles.couponApply}>
        <div className={styles.couponApplyLabel}>Coupon</div>
        <div className={styles.couponApplyInput}>
          <FormCheckbox
            checkboxControllerClassName={styles.couponApplyCheckbox}
            checkboxLabelClassName={styles.couponApplyCheckboxLabel}
            clickOnLabel={true}
            clickOnLabelCallback={handleApply}
            disabled={loading}
            id={productId}
            value={applyCoupon}
            onChange={handleApply}
            label={
              <>
                {applyCoupon ? (
                  <span className={styles.couponAppliedMessage}>
                    <b>{`${
                      autoClipCoupon.discount.type === 1
                        ? `${autoClipCoupon.discount.value}% Off`
                        : `$${autoClipCoupon.discount.value} Off`
                    }`}</b>{' '}
                    coupon applied
                  </span>
                ) : (
                  <>
                    Apply{' '}
                    <b>{`${
                      autoClipCoupon.discount.type === 1
                        ? `${autoClipCoupon.discount.value}% Off`
                        : `$${autoClipCoupon.discount.value} Off`
                    }`}</b>{' '}
                    coupon
                  </>
                )}
                &nbsp;
              </>
            }
          />
          {!applyCoupon && (
            <AnchorDialog
              iconClassName={styles.couponApplyTermsIcon}
              contentClassName={styles.couponApplyTermsContent}
              anchorContent={(open: MouseEventHandler<HTMLSpanElement>) => (
                <span
                  data-testid={`coupon-apply-dialog-trigger`}
                  className={clsx(styles.couponApplyTerms, {
                    [styles.couponApplyTermsDisabled]: loading,
                  })}
                  onClick={async (e) => {
                    if (onGetTermsContent) {
                      const terms = await onGetTermsContent();
                      setTermsContent(terms);
                      open(e);
                    }
                  }}
                >
                  Terms
                </span>
              )}
            >
              <div dangerouslySetInnerHTML={{ __html: termsContent ?? '' }} />
            </AnchorDialog>
          )}
        </div>
      </div>
    </>
  );
};

export default CouponApply;
